import { useEffect, useRef, useState } from "react";
import DataTable from "../../Components/Common/datatable/DataTable";
import { Box, IconButton, Skeleton, Tooltip } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import VisibilitySharpIcon from "@mui/icons-material/VisibilitySharp";
import TrackerDialog from "./TrackerDialog";
import ApiService from "../../utils/fetch";
import { safeTimestamp } from "../../Components/Common/DateFormatter";

function AccessTracker() {
  const gridRef = useRef(null);
  const [ticketDetails, setTicketDetails] = useState([]);
  const [open, setOpen] = useState(false);
  const [trackerData, setTrackerData] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchTrackerData();
  }, []);

  const fetchTrackerData = async () => {
    setLoading(true);
    try {
      const response = await ApiService.get(`v1/access-ticket/get-all-details`);
      if (response.status === 200) {
        setTrackerData(response.data);
      }
    } catch (error) {
      console.error("Error fetching tracker data:", error);
    } finally {
      setLoading(false);
    }
  };

  function dateComparator(date1, date2) {
    // Convert both ISO 8601 strings to timestamps (milliseconds since Unix Epoch)
    const date1Number = _isoDateToTimestamp(date1);
    const date2Number = _isoDateToTimestamp(date2);

    // Handle null or invalid dates
    if (date1Number === null && date2Number === null) {
      return 0;
    }
    if (date1Number === null) {
      return -1;
    }
    if (date2Number === null) {
      return 1;
    }

    // Compare timestamps
    return date1Number - date2Number;
  }

  function _isoDateToTimestamp(isoDate) {
    if (!isoDate || typeof isoDate !== "string") {
      return null;
    }

    const date = new Date(isoDate);
    // Check if Date parsing failed
    if (isNaN(date.getTime())) {
      return null;
    }

    return date.getTime();
  }

  const ColumnDefinition = [
    {
      field: "alIdentifier",
      headerName: "AL Request ID",
      sortable: true,
      unSortIcon: true,
      width: 200,
      minWidth: 200,
      filter: "agMultiColumnFilter",
      filterParams: {
        filters: [
          {
            filter: "agTextColumnFilter",
          },
          {
            filter: "agSetColumnFilter",
          },
        ],
      },
      cellRenderer: (params) => (
        <Tooltip title={`${params.data.alIdentifier}`} arrow>
          <div>{params.data.alIdentifier}</div>
        </Tooltip>
      ),
    },
    {
      field: "requestType",
      headerName: "Request Type",
      sortable: true,
      unSortIcon: true,
      width: 200,
      minWidth: 200,
      filter: "agMultiColumnFilter",
      filterParams: {
        filters: [
          {
            filter: "agTextColumnFilter",
          },
          {
            filter: "agSetColumnFilter",
          },
        ],
      },
      cellRenderer: (params) => (
        <Tooltip title={`${params.data.requestType}`} arrow>
          <div>{params.data.requestType}</div>
        </Tooltip>
      ),
    },
    // {
    //   field: "requestedRole",
    //   headerName: "Requested Role",
    //   sortable: true,
    //   unSortIcon: true,
    //   width: 200,
    //   minWidth: 200,
    //   filter: "agMultiColumnFilter",
    //   filterParams: {
    //     filters: [
    //       {
    //         filter: "agTextColumnFilter",
    //       },
    //       {
    //         filter: "agSetColumnFilter",
    //       },
    //     ],
    //   },
    //   cellRenderer: (params) => (
    //     <Tooltip title={`${params.data.requestedRole}`} arrow>
    //       <div>{params.data.requestedRole}</div>
    //     </Tooltip>
    //   ),
    // },
    {
      field: "requestedRole",
      headerName: "Requested Role",
      sortable: true,
      unSortIcon: true,
      width: 420,
      minWidth: 420,
      flex: 1,
      filter: "agMultiColumnFilter",
      filterParams: {
        filters: [
          { filter: "agTextColumnFilter" },
          { filter: "agSetColumnFilter" },
        ],
      },
      wrapText: true,
      autoHeight: true,
      cellStyle: {
        whiteSpace: "normal",
        lineHeight: "1.4",
        paddingTop: "8px",
        paddingBottom: "8px",
      },
      valueGetter: (params) => {
        // Optional: Clean up the string a bit
        if (!params.data.requestedRole) return "";
        return params.data.requestedRole
          .split(", ")
          .map((role) => role.trim())
          .join("\n");
      },
      cellRenderer: (params) => {
        if (!params.value) return null;

        const roles = params.value.split("\n");

        return (
          <div style={{ padding: "4px 0" }}>
            {roles.map((role, index) => (
              <div
                key={index}
                style={{
                  marginBottom: "4px",
                  fontSize: "0.875rem",
                }}
              >
                {index + 1}. {role}
              </div>
            ))}
          </div>
        );
      },
    },
    {
      field: "raisedBy",
      headerName: "Requested By",
      sortable: true,
      unSortIcon: true,
      width: 200,
      minWidth: 200,
      filter: "agMultiColumnFilter",
      filterParams: {
        filters: [
          {
            filter: "agTextColumnFilter",
          },
          {
            filter: "agSetColumnFilter",
          },
        ],
      },
      cellRenderer: (params) => (
        <Tooltip title={`${params.data.raisedBy}`} arrow>
          <div>{params.data.raisedBy}</div>
        </Tooltip>
      ),
    },
    {
      field: "firstName",
      headerName: "Requester For",
      sortable: true,
      unSortIcon: true,
      width: 200,
      minWidth: 200,
      valueGetter: (params) => {
        const { firstName, lastName } = params.data;
        return `${firstName || ""} ${lastName || ""}`;
      },
      cellRenderer: (params) => {
        const display = `${params.data.firstName || ""} ${
          params.data.lastName || ""
        }`;
        return (
          <Tooltip title={display} arrow>
            <div>
              <span>
                {params.data.firstName || ""} {params.data.lastName || ""}
              </span>
            </div>
          </Tooltip>
        );
      },
      filter: "agMultiColumnFilter",
      filterParams: {
        filters: [
          {
            filter: "agTextColumnFilter",
          },
          {
            filter: "agSetColumnFilter",
          },
        ],
      },
    },
    {
      field: "ticketNo",
      headerName: "Ticket ID",
      sortable: true,
      unSortIcon: true,
      width: 200,
      minWidth: 200,
      filter: "agMultiColumnFilter",
      filterParams: {
        filters: [
          {
            filter: "agTextColumnFilter",
          },
          {
            filter: "agSetColumnFilter",
          },
        ],
      },
      cellRenderer: (params) => (
        <Tooltip title={`${params.data.ticketNo}`} arrow>
          <div>{params.data.ticketNo}</div>
        </Tooltip>
      ),
    },
    // Update the creationStatus column definition
    {
      field: "creationStatus",
      headerName: "Status",
      sortable: true,
      unSortIcon: true,
      width: 200,
      minWidth: 200,
      filter: "agMultiColumnFilter",
      filterParams: {
        filters: [
          {
            filter: "agTextColumnFilter",
          },
          {
            filter: "agSetColumnFilter",
          },
        ],
      },
      cellRenderer: (params) => {
        const status = params.value === false ? "Inactive" : "Active";
        return (
          <Tooltip title={status} arrow>
            <div
              style={{
                color: params.value === false ? "#d32f2f" : "#2e7d32",
                fontWeight: 500,
              }}
            >
              {status}
            </div>
          </Tooltip>
        );
      },
    },
    {
      field: "createdAt",
      headerName: "Created Date UTC",
      sortable: true,
      unSortIcon: true,
      width: 210,
      minWidth: 210,
      filterParams: {
        comparator: function (filterDate, cellValue) {
          if (!cellValue) return -1;

          const cellDate = new Date(cellValue);
          // Strip time and compare only YYYY-MM-DD
          const cellDateOnly = new Date(
            cellDate.getFullYear(),
            cellDate.getMonth(),
            cellDate.getDate(),
          );

          return cellDateOnly.getTime() - filterDate.getTime();
        },
        filterOptions: ["equals", "inRange"],
        defaultOption: "equals",
        buttons: ["apply", "reset"],
        closeOnApply: true,
        onApply: (params) => params.api.hidePopupMenu(),
        onReset: (params) => params.api.hidePopupMenu(),
      },
      filter: "agDateColumnFilter",
      comparator: dateComparator,
      sort: "desc",
      valueFormatter: (params) => {
        return safeTimestamp(params.value);
      },
    },
    {
      field: "description",
      headerName: "Business Justification",
      sortable: true,
      unSortIcon: true,
      width: 250,
      minWidth: 250,
      filter: "agMultiColumnFilter",
      filterParams: {
        filters: [
          {
            filter: "agTextColumnFilter",
          },
          {
            filter: "agSetColumnFilter",
          },
        ],
      },
      cellRenderer: (params) => (
        <Tooltip title={`${params.data.description}`} arrow>
          <div>{params.data.description}</div>
        </Tooltip>
      ),
    },
    {
      field: "ticketStatus",
      headerName: "Ticket Status",
      sortable: true,
      unSortIcon: true,
      width: 180,
      minWidth: 180,
      filter: "agMultiColumnFilter",
      filterParams: {
        filters: [
          {
            filter: "agTextColumnFilter",
          },
          {
            filter: "agSetColumnFilter",
          },
        ],
      },
      cellRenderer: (params) => (
        <Tooltip title={`${params.data.ticketStatus}`} arrow>
          <div>{params.data.ticketStatus}</div>
        </Tooltip>
      ),
    },
    {
      headerName: "View Details",
      sortable: false,
      width: 140,
      minWidth: 140,
      resizable: false,
      cellRenderer: (params) => {
        // determine if row is IBP-only
        const rt = params.data.requestType;
        const isIBPOnly = Array.isArray(rt)
          ? rt.length === 1 && rt[0] === "IBP Access"
          : rt === "IBP Access";

        const title = isIBPOnly ? "Autonomous Requests Only" : "";

        return (
          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "center",
              width: "100%",
              height: "100%",
            }}
          >
            <Tooltip title={title} arrow>
              <span
                onClick={() => {
                  if (!isIBPOnly) handleOpen(params.data);
                }}
                style={{
                  display: "inline-flex",
                  alignItems: "flex-start",
                  justifyContent: "center",
                  width: "100%",
                  height: "100%",
                  cursor: isIBPOnly ? "not-allowed" : "pointer",
                  color: isIBPOnly ? "rgba(0,0,0,0.38)" : "inherit",
                }}
              >
                <span style={{ lineHeight: 4 }}>
                  <VisibilitySharpIcon fontSize="small" />
                </span>
              </span>
            </Tooltip>
          </div>
        );
      },
    },
  ];

  const handleOpen = async (rowData) => {
    const id = rowData.id;

    const fetchData = async () => {
      try {
        const response = await ApiService.get(
          `v1/access-ticket/get-access-ticket-detail?id=${id}`,
        );
        setTicketDetails(response.data);
      } catch (error) {
        console.error("Failed to fetch ticket details:", error);
      }
    };

    fetchData();
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setTicketDetails(null);
  };

  const onGrid = (params) => {
    const gridApi = params.api;
    if (gridApi) {
      gridApi.sizeColumnsToFit();
    } else {
      console.error("Grid API is not available.");
    }
  };

  const pagination = true;
  const paginationPageSize = 10;
  const paginationPageSizeSelector = [5, 10, 20, 50, 100];

  return (
    <>
      <Box
        sx={{
          mt: -3,
          ml: 1,
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
        }}
      >
        <Tooltip title="Refresh">
          <span>
            <IconButton
              onClick={fetchTrackerData}
              disabled={loading}
              sx={{
                ml: 2,
                backgroundColor: loading ? "grey.300" : "transparent",
                "&:hover": { backgroundColor: "grey.100" },
              }}
            >
              <RefreshIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
      <Box sx={{ marginTop: 0 }}>
        {loading ? (
          <Skeleton
            variant="rectangular"
            height={"70vh"}
            animation="pulse"
            style={{ marginBottom: "16px", borderRadius: "8px" }}
          />
        ) : (
          <DataTable
            ref={gridRef}
            columns={ColumnDefinition}
            rows={trackerData || []}
            tableHeight={"70vh"}
            suppressMovableColumns={true}
            suppressHorizontalScroll={false}
            suppressPaginationPanel={false}
            pagination={pagination}
            paginationPageSize={paginationPageSize}
            paginationPageSizeSelector={paginationPageSizeSelector}
            onGridReady={onGrid}
            rowSelection="multiple"
          />
        )}
      </Box>
      <TrackerDialog
        open={open}
        onClose={handleClose}
        ticketDetails={ticketDetails}
      />
    </>
  );
}

export default AccessTracker;
